import { useParams } from '@tanstack/react-router';
import { IHistoryItem } from '_/common/history/history.types';
import { IComment } from '_/common/types/comments.types';
import { IReadmeVersion } from '_/common/types/readme.types';
import NotExists from '_/components/common/NotExists';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { IFlow } from '_/features/flows/common/types/flow.types';
import { IFlowSegment } from '_/features/flows/common/types/flowSegments.types';
import { IFlowPageData } from '_/features/flows/flowPage/flowPage.types';
import { useCollections } from '_/hooks/useCollections';
import { useFlow, useFlowComments, useFlowHistory, useFlowReadmeVersions } from '_/hooks/useFlows';
import { ICollection } from '_/types/collections/collections.types';
import React, { createContext, useContext, ReactNode } from 'react';

interface FlowContextValue {
  projectId: string | undefined;
  projectSlug: string | undefined;
  flowSlug: string | undefined;
  flowId: string | undefined;
  selectedFlow: IFlowPageData | null;
  flowSegments: IFlowSegment[];
  flowComments: IComment[];
  flowHistory: { items: IHistoryItem<IFlow>[]; cursor: string } | null;
  flowReadmeVersions: { items: IReadmeVersion[] } | null;
  collections: ICollection[];
  isLoading: boolean;
  isLoadingSegments: boolean;
  isLoadingComments: boolean;
  isLoadingHistory: boolean;
  isLoadingReadmeVersions: boolean;
  isLoadingCollections: boolean;
  notFound: boolean;
}

const FlowContext = createContext<FlowContextValue | undefined>(undefined);

export function FlowProvider({ children }: { children: ReactNode }) {
  const params = useParams({ strict: false }) as Partial<{
    projectSlug?: string;
    flowSlug?: string;
  }>;
  const projectSlug = params.projectSlug || '';
  const flowSlug = params.flowSlug || '';
  const { selectedOrgSlug } = useOrganization();
  const { selectedProject } = useProject();
  const projectId = selectedProject?.id;

  // Fetch the flow (includes segments and readme via adapter)
  const { data: selectedFlow, isLoading } = useFlow(projectId, flowSlug);
  const flowId = selectedFlow?.id;

  // Fetch project collections for flow definition
  const { data: collections, isLoading: isLoadingCollections } = useCollections(projectId);

  // Fetch related data using the flow ID
  const { data: flowComments, isLoading: isLoadingComments } = useFlowComments(flowId);
  const { data: flowHistory, isLoading: isLoadingHistory } = useFlowHistory(flowId);
  const { data: flowReadmeVersions, isLoading: isLoadingReadmeVersions } = useFlowReadmeVersions(flowId);

  // Determine if flow is not found: when loading is done, we have a flowSlug, but no matching flow
  const notFound = !isLoading && !!flowSlug && flowSlug !== '_new' && !selectedFlow;

  const value: FlowContextValue = {
    projectId: projectId,
    projectSlug: projectSlug,
    flowSlug: flowSlug,
    flowId: flowId,
    selectedFlow: selectedFlow || null,
    flowSegments: selectedFlow?.segments || [],
    flowComments: flowComments || [],
    flowHistory: flowHistory || null,
    flowReadmeVersions: flowReadmeVersions || { items: [] },
    collections: collections || [],
    isLoading: isLoading,
    isLoadingSegments: false, // Segments are loaded with the flow
    isLoadingComments: isLoadingComments,
    isLoadingHistory: isLoadingHistory,
    isLoadingReadmeVersions: isLoadingReadmeVersions,
    isLoadingCollections: isLoadingCollections,
    notFound: notFound,
  };

  // If flow not found, render NotExists page
  if (notFound) {
    return (
      <NotExists entityType="Flow" backUrl={`/orgs/${selectedOrgSlug}/projects/${projectSlug}/flows`} backLabel="Back to Flows" />
    );
  }

  return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>;
}

export function useFlowContext(): FlowContextValue {
  const context = useContext(FlowContext);
  if (context === undefined) {
    throw new Error('useFlowContext must be used within a FlowProvider');
  }
  return context;
}
